<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useFacebookAdsLogin } from 'dashboard/composables/useFacebookAdsLogin';
import { useMetaCampaignsAdAccountsStore } from 'dashboard/stores/metaCampaignsAdAccounts';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  adAccount: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const adAccountsStore = useMetaCampaignsAdAccountsStore();
const { login } = useFacebookAdsLogin();

const SYNC_COOLDOWN_MS = 60000;

const isReconnecting = ref(false);
const isDisconnecting = ref(false);
const isSyncing = ref(false);
const syncCooldownActive = ref(false);

const needsReauth = computed(() => props.adAccount.status === 'needs_reauth');
const isSyncingHistory = computed(
  () =>
    props.adAccount.sync_progress_percent !== null &&
    props.adAccount.sync_progress_percent !== undefined
);

const statusLabel = computed(() =>
  t(
    `META_CAMPAIGNS.AD_ACCOUNT_CARD.STATUS.${props.adAccount.status?.toUpperCase()}`
  )
);

const reconnect = async () => {
  isReconnecting.value = true;
  try {
    const shortLivedToken = await login();
    await adAccountsStore
      .oauthAdAccounts(shortLivedToken)
      .then(({ access_token: token }) =>
        adAccountsStore.reconnect(props.adAccount.id, token)
      );
    useAlert(t('META_CAMPAIGNS.AD_ACCOUNT_CARD.RECONNECT_SUCCESS'));
  } catch (error) {
    useAlert(t('META_CAMPAIGNS.AD_ACCOUNT_CARD.RECONNECT_ERROR'));
  } finally {
    isReconnecting.value = false;
  }
};

const disconnect = async () => {
  isDisconnecting.value = true;
  try {
    await adAccountsStore.disconnect(props.adAccount.id);
  } finally {
    isDisconnecting.value = false;
  }
};

const syncNow = async () => {
  isSyncing.value = true;
  syncCooldownActive.value = true;
  try {
    await adAccountsStore.syncNow(props.adAccount.id);
    useAlert(t('META_CAMPAIGNS.AD_ACCOUNT_CARD.SYNC_STARTED'));
  } catch (error) {
    useAlert(t('META_CAMPAIGNS.AD_ACCOUNT_CARD.SYNC_ERROR'));
  } finally {
    isSyncing.value = false;
    setTimeout(() => {
      syncCooldownActive.value = false;
    }, SYNC_COOLDOWN_MS);
  }
};
</script>

<template>
  <div
    class="flex flex-col gap-3 rounded-xl border border-n-strong bg-n-alpha-3 p-4"
  >
    <div class="flex items-center justify-between gap-3">
      <div class="flex flex-col gap-0.5">
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium text-n-slate-12">{{
            adAccount.name
          }}</span>
          <span
            class="text-xs px-2 py-0.5 rounded-full font-medium"
            :class="
              needsReauth
                ? 'bg-n-ruby-3 text-n-ruby-11'
                : 'bg-n-teal-3 text-n-teal-11'
            "
          >
            {{ statusLabel }}
          </span>
        </div>
        <span class="text-xs text-n-slate-10">
          {{ adAccount.meta_ad_account_id }} · {{ adAccount.currency }}
        </span>
        <span v-if="adAccount.last_synced_at" class="text-xs text-n-slate-9">
          {{ t('META_CAMPAIGNS.AD_ACCOUNT_CARD.LAST_SYNCED') }}
          {{ new Date(adAccount.last_synced_at).toLocaleString() }}
        </span>
      </div>
      <div class="flex items-center gap-2 shrink-0">
        <Button
          v-if="needsReauth"
          size="sm"
          :label="t('META_CAMPAIGNS.AD_ACCOUNT_CARD.RECONNECT')"
          :is-loading="isReconnecting"
          @click="reconnect"
        />
        <Button
          v-else
          size="sm"
          variant="faded"
          color="slate"
          icon="i-lucide-refresh-cw"
          :label="t('META_CAMPAIGNS.AD_ACCOUNT_CARD.SYNC_NOW')"
          :is-loading="isSyncing"
          :disabled="syncCooldownActive"
          @click="syncNow"
        />
        <Button
          size="sm"
          variant="faded"
          color="ruby"
          :label="t('META_CAMPAIGNS.AD_ACCOUNT_CARD.DISCONNECT')"
          :is-loading="isDisconnecting"
          @click="disconnect"
        />
      </div>
    </div>

    <div v-if="isSyncingHistory" class="flex flex-col gap-1">
      <div class="flex items-center justify-between text-xs text-n-slate-10">
        <span>{{
          t('META_CAMPAIGNS.AD_ACCOUNT_CARD.SYNC_PROGRESS_LABEL')
        }}</span>
        <span>{{ adAccount.sync_progress_percent }}%</span>
      </div>
      <div class="h-1.5 rounded-full bg-n-weak overflow-hidden">
        <div
          class="h-full bg-n-brand rounded-full transition-all duration-500"
          :style="{ width: `${adAccount.sync_progress_percent}%` }"
        />
      </div>
    </div>
  </div>
</template>
